import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2, X } from 'lucide-react';
import { ProductPreview } from '@/components/editor/ProductPreview';
import { useDesignStore } from '@/stores/designStore';
import {
  isSystemLayer,
  SYSTEM_LAYER_NAMES,
} from '@/services/canvas/systemLayerManager';
import { cn } from '@/lib/utils';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { useObjectOperations } from '@/hooks/useObjectOperations';
import { useDirectSaleEditorBridge } from './useDirectSaleEditorBridge';
import type { DirectSaleTool } from './DirectSaleDesignToolbar';
import { DirectSaleToolPanel } from './DirectSaleToolPanel';
import {
  DirectSaleTextPanel,
  type DirectSaleTextTab,
} from './panels/DirectSaleTextPanel';
import { DirectSaleMobileEditBar } from './DirectSaleMobileEditBar';
import { useIsPhone } from './useIsPhone';
import { useDirectSaleInlineText } from './useDirectSaleInlineText';
import { DirectSaleAddModal } from './DirectSaleAddModal';
import {
  DIRECT_SALE_UPLOAD_ACCEPT,
  DIRECT_SALE_UPLOAD_MAX_MB,
  useDirectSaleUpload,
} from './useDirectSaleUpload';
import { DirectSaleUploadPanel } from './panels/DirectSaleUploadPanel';
import { DirectSaleMotifPanel } from './panels/DirectSaleMotifPanel';
import { DirectSalePositionPills } from './DirectSalePositionPills';
import {
  DirectSaleMobileBar,
  DirectSaleMobileOverlay,
  type DirectSaleMobileSheet,
} from './DirectSaleMobileChrome';
import { sizesForColor } from './direct-sale-sizes';
import { useDirectSaleDesktopChrome } from './desktop/useDirectSaleDesktopChrome';
import { positionsWithArtwork } from './positions-with-artwork';
import { CANVAS_SIZING } from '@/config/constants';
import { getApiErrorCode } from '@/lib/api-errors';
import {
  getConfiguredPositionTemplates,
  transformProduct,
} from '@/lib/product-transformer';

/** Phone: a drag this long (canvas px) on a text being typed in moves it. */
const DRAG_ENDS_TYPING_PX = 14;
/** Two taps this close together on the same text are a double tap. */
const DOUBLE_TAP_MS = 350;
const isTextType = (type?: string) =>
  type === 'textbox' || type === 'i-text' || type === 'text';

/**
 * Step 2: the customer designs the product they picked.
 *
 * The canvas, mockup rendering and undo history are the existing editor core,
 * embedded rather than reimplemented. Only the surrounding chrome — colour,
 * size, tool panels and which positions are offered — is Direct Sale's own.
 *
 * Unlike the Seller flow, editing surfaces are Direct Sale's own, not the
 * core's TextPropertiesBar/ImagePropertiesBar — two parallel editing
 * surfaces would disagree on fonts and size models.
 *
 * Two chromes share the one canvas. The phone's (sheets, bottom card) is
 * switched by CSS and `isPhone`; the desktop's (tool rail, drawer, object
 * bar, purchase column — after Spreadshirt's designer) is only mounted from
 * 981px. Neither contains the canvas: ProductPreview and its ancestors keep
 * their place in the tree whatever the chrome does, or the editor would
 * remount and the design would be gone.
 */
export function DirectSaleDesignStep() {
  const { t } = useTranslation();
  const { product, isPending, isError, error } = useDirectSaleEditorBridge();
  const goToStep = useDirectSaleStore((s) => s.goToStep);

  const colorId = useDirectSaleStore((s) => s.colorId);
  const setDecoratedPositions = useDirectSaleStore(
    (s) => s.setDecoratedPositions
  );

  const masterPositions = useCanvasStateManager((s) => s.masterPositions);
  const canvas = useCanvasStateManager((s) => s.canvas);
  const activePosition = useProductSelectionStore((s) => s.activePosition);

  // Which tool panel is open. Ephemeral by design — a resumed session with
  // existing artwork must not come back with a panel already open.
  const [activeTool, setActiveTool] = useState<DirectSaleTool | null>(null);
  // The phone's own sheets (add / colour / details / buy).
  const [mobileSheet, setMobileSheet] = useState<DirectSaleMobileSheet | null>(
    null
  );

  // Mirrors the Seller MainLayout's selection logic for the editing surfaces.
  const selectedObjects = useDesignStore((s) => s.selectedObjects);
  const hasTextSelected = selectedObjects.some((o) => isTextType(o.type));
  const hasImageSelected = selectedObjects.some(
    (o) => o.type === 'image' && !isSystemLayer(o.name)
  );

  // Layout-neutral: only picks behaviour and chrome, the canvas stays put.
  const isPhone = useIsPhone();
  // For the canvas handlers, which are registered once per canvas.
  const isPhoneRef = useRef(isPhone);
  isPhoneRef.current = isPhone;
  // Phone: the section the text sheet opens on (the edit bar's pill).
  const [textTab, setTextTab] = useState<DirectSaleTextTab>('write');

  // Selecting a text opens nothing by itself, on either chrome: the phone
  // enters edit mode and the desktop shows the object bar. A panel covering
  // the product is only opened by the customer.

  // Phone: a tap on the product asks what should go there.
  const [addModalOpen, setAddModalOpen] = useState(false);
  // Something of Direct Sale's own owns the screen; taps on the product
  // belong to it then, not to the "add" question. Read through a ref by the
  // canvas handlers, which are registered once.
  const chromeBusy =
    activeTool !== null || mobileSheet !== null || addModalOpen;
  const chromeBusyRef = useRef(chromeBusy);
  chromeBusyRef.current = chromeBusy;
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;

  const editMode =
    isPhone && !chromeBusy && (hasTextSelected || hasImageSelected);

  /** Back to the clean preview: nothing selected, no handles, no frame. */
  const deselect = () => {
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  };

  // A placement panel left open across a position switch would drop its
  // upload/motif onto the position the customer just left — close them.
  // The text panel handles the switch itself by rebinding.
  useEffect(() => {
    setActiveTool((prev) =>
      prev === 'upload' || prev === 'motifs' ? null : prev
    );
  }, [activePosition]);

  /**
   * Persist canvas edits into masterPositions as they happen. The manager
   * only writes a position's json in switchPosition/saveCurrentPosition —
   * both Seller-flow paths. Without this, a customer designing a single
   * position (never toggling) keeps json === null, decoratedPositions stays
   * empty and the checkout step never unlocks.
   */
  useEffect(() => {
    if (!canvas) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    // Deferred on purpose: object:added fires synchronously inside
    // canvas.add(), before the editor has finished configuring the object —
    // a synchronous serialize at that point misses it (verified in-browser:
    // upload alone left json.objects empty until the first modify). One
    // macrotask later the add sequence is complete; the delay also
    // debounces bursts of events into a single save.
    const save = (event?: { target?: { name?: string } }) => {
      // Canvas construction adds the system layers (background, mockup,
      // border) and fires object:added for each. Saving on those would
      // serialize an empty user state OVER the stored one — before the
      // editor's own remount-restore (loadSavedPosition) gets to read it,
      // silently destroying the design on every step return.
      if (isSystemLayer(event?.target?.name)) return;

      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        // Cross-store call: never let a canvas save failure break the step
        // (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
        try {
          useCanvasStateManager.getState().saveCurrentPosition();
        } catch {
          // Canvas not ready for serialization; the next change retries.
        }
      }, 50);
    };

    canvas.on('object:added', save);
    canvas.on('object:modified', save);
    canvas.on('object:removed', save);
    return () => {
      if (timer) clearTimeout(timer);
      canvas.off('object:added', save);
      canvas.off('object:modified', save);
      canvas.off('object:removed', save);
      // The canvas is going away with this step; a selection left behind
      // would keep the editing surfaces visible on the next mount with
      // nothing actually selected.
      useDesignStore.getState().setSelectedObjects([]);
    };
  }, [canvas]);

  /**
   * Only positions with a maintained surcharge and a calibrated template are
   * offered. An unpriced position cannot be quoted, so letting the customer
   * decorate it would strand them at checkout with an error they cannot act
   * on; a position without a calibrated template (NEEDS_REVIEW, uncalibrated)
   * has no print area to design on and is never decorated or charged (R13).
   */
  const sellablePositions = useMemo(() => {
    if (!product) return [];
    const configured = new Set(
      getConfiguredPositionTemplates(
        transformProduct(product).mockupTemplates
      ).map((template) => template.positionKey)
    );
    return (product.positions ?? []).filter(
      (p) => p.printSurcharge != null && configured.has(p.slug)
    );
  }, [product]);

  const selectedColor = product?.colors?.find((c) => c.id === colorId) ?? null;

  // Text is typed straight onto the product, on both chromes. A placeholder
  // left untouched is removed again: it must never count as a design, or
  // "Dein Text" would be printed.
  const inlineText = useDirectSaleInlineText({
    canvas,
    enabled: true,
    productColor: selectedColor?.hexCode,
  });

  // Phone: the upload tile opens the system's picker (photo library, camera,
  // files) at once — a drop zone in between means nothing on a phone.
  const phoneUpload = useDirectSaleUpload();
  const uploadInputRef = useRef<HTMLInputElement>(null);

  /**
   * One way into a tool, for the bottom card's tiles and the add modal alike.
   * Synchronous inside the tap on purpose: iOS opens the keyboard and the
   * file picker only within the user gesture.
   */
  const openTool = (tool: DirectSaleTool) => {
    setAddModalOpen(false);
    if (isPhone && tool === 'text') {
      inlineWantedRef.current = true;
      try {
        inlineText.start();
      } finally {
        inlineWantedRef.current = false;
      }
      return;
    }
    if (isPhone && tool === 'upload') {
      phoneUpload.clearError();
      uploadInputRef.current?.click();
      return;
    }
    setActiveTool(tool);
  };

  // Phone: what a tap on the canvas means.
  /** Opens the text sheet on its text field for the tapped text. */
  const openTextField = () => {
    setTextTab('write');
    setActiveTool('text');
  };
  /**
   * Fabric enters editing on its own when a selected text is tapped again
   * (after our tap handler ran). On the phone typing on the product is only
   * for a text just created: everything else goes through the sheet. Set
   * around inlineText.start(), read by the editing:entered handler.
   */
  const inlineWantedRef = useRef(false);
  const openTextFieldRef = useRef(openTextField);
  openTextFieldRef.current = openTextField;
  useEffect(() => {
    if (!canvas) return;
    // Fabric clears the selection on mouse:down, so whether the tap landed
    // on a selection has to be noted before that.
    let hadSelection = false;
    const noteSelection = () => {
      hadSelection = canvas.getActiveObject() != null;
    };
    let lastTap: { target: unknown; at: number } = { target: null, at: 0 };
    const onTap = (e: {
      isClick?: boolean;
      target?: { name?: string; type?: string; __corner?: string };
    }) => {
      // A drag, a pinch or a handle is not a tap.
      if (!isPhoneRef.current || !e.isClick) return;
      const target = e.target;
      // A tool sheet is up: a tap on the empty product closes it — the
      // check mark is not the only way out. A tap on an object stays with
      // the sheet's own rules below.
      if (activeToolRef.current !== null) {
        if (!target || isSystemLayer(target.name)) setActiveTool(null);
        return;
      }
      if (chromeBusyRef.current) return;
      if (target && !isSystemLayer(target.name)) {
        // An object. One tap selects it — the frame with its handles and
        // the edit bar below. Two taps in quick succession on a text open
        // the text field to change the words; typing on the product offers
        // no caret for corrections.
        const now = Date.now();
        const isDouble =
          lastTap.target === target && now - lastTap.at < DOUBLE_TAP_MS;
        lastTap = { target, at: now };
        if (
          isDouble &&
          !target.__corner &&
          canvas.getObjects().includes(target as never) &&
          isTextType(target.type)
        ) {
          lastTap = { target: null, at: 0 };
          openTextFieldRef.current();
        }
        return;
      }
      // Empty product. With a selection this tap meant "let go of it" —
      // only a tap on a calm product asks what to add.
      if (!hadSelection) setAddModalOpen(true);
    };
    // While a text is being typed in, Fabric locks its position and hides
    // its handles: a drag would select characters. A customer who drags
    // their text wants it moved — so the drag ends the typing, the handles
    // come back and the next drag moves it.
    let downAt: { x: number; y: number } | null = null;
    const noteDown = (e: { viewportPoint?: { x: number; y: number } }) => {
      downAt = e.viewportPoint ?? null;
    };
    const endTypingOnDrag = (e: {
      viewportPoint?: { x: number; y: number };
    }) => {
      if (!isPhoneRef.current || !downAt || !e.viewportPoint) return;
      const active = canvas.getActiveObject() as
        | { isEditing?: boolean; exitEditing?: () => void }
        | undefined;
      if (!active?.isEditing) return;
      const moved = Math.hypot(
        e.viewportPoint.x - downAt.x,
        e.viewportPoint.y - downAt.y
      );
      if (moved < DRAG_ENDS_TYPING_PX) return;
      downAt = null;
      active.exitEditing?.();
      canvas.requestRenderAll();
    };
    const forgetDown = () => {
      downAt = null;
    };

    canvas.on('mouse:down:before', noteSelection);
    canvas.on('mouse:down', noteDown);
    canvas.on('mouse:move', endTypingOnDrag);
    // Fabric starts typing on a second tap of a selected text, after onTap
    // ran (the target's own mouseup follows the canvas event). Not on the
    // phone: only a text just created is typed on the product.
    const keepTypingInTheSheet = (e: {
      target?: { exitEditing?: () => void };
    }) => {
      if (!isPhoneRef.current || inlineWantedRef.current) return;
      e.target?.exitEditing?.();
    };
    canvas.on('text:editing:entered', keepTypingInTheSheet);
    canvas.on('mouse:up', onTap);
    canvas.on('mouse:up', forgetDown);
    return () => {
      canvas.off('mouse:down:before', noteSelection);
      canvas.off('mouse:down', noteDown);
      canvas.off('mouse:move', endTypingOnDrag);
      canvas.off('text:editing:entered', keepTypingInTheSheet);
      canvas.off('mouse:up', onTap);
      canvas.off('mouse:up', forgetDown);
    };
  }, [canvas]);

  /**
   * The free space above and below the product. Not the product: a tap
   * there never asks what to add — it only lets go of a selection, the way
   * a tap beside an object does anywhere.
   */
  const onCanvasBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPhone) return;
    const el = e.target as HTMLElement;
    if (
      el.closest(
        'button, a, input, .canvas-container, [role="dialog"], [role="alert"], [role="status"]'
      )
    ) {
      return;
    }
    if (chromeBusy) return;
    if (canvas?.getActiveObject()) deselect();
  };

  // Only the sizes the chosen colour carries: the quote refuses any other.
  const sizes = useMemo(
    () => sizesForColor(product, colorId),
    [product, colorId]
  );

  /**
   * Report which positions actually carry artwork. This is what makes the
   * checkout step reachable and what the price breakdown is built from, so it
   * has to follow the canvas rather than the customer's intent.
   */
  useEffect(() => {
    const decorated = positionsWithArtwork(
      sellablePositions,
      masterPositions
    ).map((position) => ({
      positionId: position.id,
      widthMm: position.printArea?.maxWidthMm ?? null,
      heightMm: position.printArea?.maxHeightMm ?? null,
    }));

    setDecoratedPositions(decorated);
  }, [masterPositions, sellablePositions, setDecoratedPositions]);

  // The desktop's chrome around the editor. Its state is kept apart from
  // `activeTool`, which steers the phone's chrome (busy state, bottom card)
  // and stays empty on the desktop.
  const desktop = useDirectSaleDesktopChrome({
    enabled: !isPhone && product != null,
    product,
    canvas,
    sellablePositions,
    sizes,
    hasTextSelected,
    startText: inlineText.start,
  });

  // The print area frame belongs to editing, on both chromes. On the preview
  // it would sit on the product like part of the design; the desktop shows
  // it while something is selected or a drawer is about to place something.
  // It is a Fabric object the editor re-creates on a position switch, hence
  // the object:added listener.
  const frameVisible = isPhone
    ? editMode || activeTool !== null
    : hasTextSelected || hasImageSelected || desktop.panelOpen;
  useEffect(() => {
    if (!canvas) return;
    const apply = () => {
      const frame = canvas
        .getObjects()
        .find((o) => o.name === SYSTEM_LAYER_NAMES.PRINT_AREA_BORDER);
      // The frame steps back behind the (solid) selection border — it marks
      // where printing is possible, the selection is what the customer is
      // working on.
      const opacity = isPhone ? 0.45 : 0.7;
      if (
        frame &&
        (frame.visible !== frameVisible || frame.opacity !== opacity)
      ) {
        frame.set({ visible: frameVisible, opacity });
        canvas.requestRenderAll();
      }
    };
    apply();
    canvas.on('object:added', apply);
    return () => {
      canvas.off('object:added', apply);
    };
  }, [canvas, frameVisible, isPhone]);

  // Zoomed, the product's box is larger than the column and the column
  // scrolls; a new zoom level starts from the middle, where the product is.
  // The editor rescales a frame after the box changed (ResizeObserver, then
  // an animation frame), so the middle is taken once more after that —
  // measured on the old scale it would sit off-centre when zooming out.
  const canvasBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = canvasBoxRef.current;
    if (!box || isPhone) return;
    const centre = () => {
      box.scrollLeft = (box.scrollWidth - box.clientWidth) / 2;
      box.scrollTop = (box.scrollHeight - box.clientHeight) / 2;
    };
    centre();
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(centre);
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [desktop.zoom, isPhone]);

  /**
   * Desktop: a file dropped onto the product goes the Direct Sale way — its
   * format and size checks, its status — instead of the editor core's own
   * drop path, which knows neither. Capture phase, so the core never sees it.
   */
  const onCanvasBoxDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (isPhone || !e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const onCanvasBoxDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (isPhone || !e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      phoneUpload.clearError();
      void phoneUpload.handleFile(file);
    }
  };

  // The window crossed the breakpoint: whatever the chrome left behind had
  // open belongs to a layout that is no longer there. Design, selection and
  // quantities stay. Skips the first run — nothing was open before it.
  const wasPhoneRef = useRef(isPhone);
  const resetDesktop = desktop.reset;
  useEffect(() => {
    if (wasPhoneRef.current === isPhone) return;
    wasPhoneRef.current = isPhone;
    setActiveTool(null);
    setMobileSheet(null);
    setAddModalOpen(false);
    resetDesktop();
    // The reset only closes things; it is stable in what it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhone]);

  if (isPending) {
    return <div className="flex-1 animate-pulse bg-paper-2" />;
  }

  if (getApiErrorCode(error) === 'product_uncalibrated') {
    // The product is not set up for designing (409): said where the editor
    // would be, with the way out (R13).
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
        <p className="text-sm text-ink-2" role="status">
          {t('errors.product_uncalibrated')}
        </p>
        <button
          type="button"
          className="rounded-control border border-line px-3 py-1.5 text-sm font-medium text-ink hover:bg-paper-2"
          onClick={() => goToStep('product')}
        >
          {t('directSale.product.chooseOther')}
        </button>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-sm text-ink-2">
          {t('directSale.product.loadFailed')}
        </p>
      </div>
    );
  }

  /**
   * Phone: the view switch under the product. Rendered by whichever bottom
   * stack is up — the bar on the preview, the edit bar while an object is
   * selected — so the product can be turned around in both. A tool sheet
   * covers the bottom and has no view switch: the sheet's object belongs to
   * the view it was made on.
   */
  const viewDots = (
    <DirectSalePositionPills
      product={product}
      sellablePositions={sellablePositions}
      variant="phone"
    />
  );

  return (
    // From 981px the editor fills exactly the window: three columns, no
    // page scroll — only drawers and the purchase column scroll inside.
    <div className="flex flex-1 flex-col min-[981px]:h-dvh min-[981px]:flex-none min-[981px]:overflow-hidden">
      {/* Desktop: three cards on a grey page, like Spreadshirt's designer. */}
      <div className="flex flex-1 flex-col min-[981px]:min-h-0 min-[981px]:flex-row min-[981px]:items-stretch min-[981px]:gap-3 min-[981px]:bg-paper-2 min-[981px]:p-3">
        {desktop.rail}
        {/* Canvas column — the shared editor core, embedded as-is. Its width
          never depends on what the chrome has open: drawers lie over it. */}
        <section className="flex flex-col bg-[rgb(var(--color-canvas-bg))] min-[981px]:min-w-0 min-[981px]:flex-1 min-[981px]:overflow-hidden min-[981px]:rounded-section min-[981px]:border min-[981px]:border-line">
          <div className="relative flex min-h-0 flex-1 flex-col">
            {desktop.overCanvas}
            {isPhone && activeTool !== null && (
              <DirectSaleToolPanel
                title={t(`directSale.design.${activeTool}`)}
                onClose={() => setActiveTool(null)}
                headerAction={
                  activeTool === 'text' && hasTextSelected ? (
                    <TextDuplicateButton />
                  ) : undefined
                }
              >
                {/* Completion closes are scoped to the initiating panel: the
                    background upload's onUploadComplete can fire after its
                    panel unmounted, and an unconditional close would shut
                    whichever panel the customer switched to meanwhile. */}
                {activeTool === 'upload' && (
                  <DirectSaleUploadPanel
                    onClose={() =>
                      setActiveTool((prev) => (prev === 'upload' ? null : prev))
                    }
                  />
                )}
                {activeTool === 'text' && (
                  <DirectSaleTextPanel
                    productColor={selectedColor?.hexCode}
                    initialTab={textTab}
                  />
                )}
                {activeTool === 'motifs' && (
                  <DirectSaleMotifPanel
                    onClose={() =>
                      setActiveTool((prev) => (prev === 'motifs' ? null : prev))
                    }
                  />
                )}
              </DirectSaleToolPanel>
            )}

            {/* Phone: the product owns the screen above the bottom card and
                stays exactly where it is, whatever opens — sheets and bars
                come and go around it. A product that jumps under the thumb
                is worse than one that is partly covered for a moment. From
                981px the preview fills the column as before. The editor
                keeps its native 500px box and only scales it with a
                transform, so the box is clipped here instead of widening the
                page on a phone. */}
            <div
              ref={canvasBoxRef}
              onClick={onCanvasBoxClick}
              onDragOverCapture={onCanvasBoxDragOver}
              onDropCapture={onCanvasBoxDrop}
              className="relative flex h-[calc(100dvh-106px)] flex-none flex-col overflow-hidden min-[981px]:h-auto min-[981px]:min-h-0 min-[981px]:flex-1 min-[981px]:overflow-auto"
            >
              {/* No drop zone on the product: on a phone "drag & drop" means
                  nothing and the bottom card is the way in; on the desktop
                  the start dialog asks, and files are dropped in the upload
                  drawer. Phone: thumb-sized object handles. */}
              <div
                className="flex min-h-0 flex-1 flex-col [&_.canvas-drop-zone]:hidden"
                // Desktop zoom: the box outgrows the column, the editor fits
                // the product into it, the column scrolls. Only a style —
                // the editor's own node stays where it is.
                style={
                  !isPhone && desktop.zoom !== 1
                    ? {
                        flex: 'none',
                        width: `${desktop.zoom * 100}%`,
                        height: `${desktop.zoom * 100}%`,
                      }
                    : undefined
                }
              >
                <ProductPreview
                  hideOverlayChips
                  // Desktop: handles the size of a mouse target, not a
                  // thumb's; the core's default is scaled with the canvas
                  // and grows past 40px on a wide window.
                  controlScreenSize={isPhone ? 40 : 26}
                  // Desktop zoom: the editor may grow the product beyond
                  // its usual cap, as far as the zoom asks.
                  maxScaleFactor={
                    isPhone
                      ? undefined
                      : CANVAS_SIZING.MAX_SCALE_FACTOR * desktop.zoom
                  }
                  controlLayout={isPhone ? 'touch' : undefined}
                  // The margin around the garment is screen a phone cannot
                  // spare; the desktop keeps the whole mockup image.
                  fitToProduct={isPhone}
                />
              </div>
              {editMode && (
                <DirectSaleMobileEditBar
                  kind={hasTextSelected ? 'text' : 'image'}
                  // Every pill opens the sheet on its section — "write"
                  // included: its text field has the caret, selection and
                  // magnifier the customer knows, which typing on the
                  // product (the first tap) cannot offer for corrections.
                  onOpenTextTab={(tab) => {
                    setTextTab(tab);
                    setActiveTool('text');
                  }}
                  onDone={deselect}
                  above={viewDots}
                />
              )}
              {/* Always there: undo, colour and the details menu do not
                  depend on what the customer is doing. */}
              <DirectSaleMobileOverlay
                product={product}
                onOpenSheet={setMobileSheet}
              />
              {/* The upload's status, over the product: the phone's picker
                  and the desktop's drop onto the product both end here. */}
              {(phoneUpload.isUploading || phoneUpload.error) && (
                <div
                  role={phoneUpload.error ? 'alert' : 'status'}
                  className={cn(
                    'absolute inset-x-3 top-[104px] z-10 flex items-center gap-2.5 rounded-card border bg-paper px-3.5 py-2.5 text-[13px] font-semibold shadow-[0_2px_8px_rgba(13,13,13,0.08)]',
                    phoneUpload.error
                      ? 'border-danger text-danger'
                      : 'border-line text-ink'
                  )}
                >
                  {phoneUpload.error ? (
                    <>
                      <span className="min-w-0 flex-1">
                        {t(
                          `directSale.design.uploadPanel.error.${phoneUpload.error}`,
                          { size: DIRECT_SALE_UPLOAD_MAX_MB }
                        )}
                      </span>
                      <button
                        type="button"
                        aria-label={t('directSale.design.close')}
                        onClick={phoneUpload.clearError}
                        className="-mr-1.5 flex h-9 w-9 flex-none items-center justify-center"
                      >
                        <X className="h-[18px] w-[18px]" />
                      </button>
                    </>
                  ) : (
                    <>
                      <Loader2 className="h-4 w-4 flex-none animate-spin" />
                      {t('directSale.design.uploadPanel.uploading')}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="max-[980px]:hidden">
              <DirectSalePositionPills
                product={product}
                sellablePositions={sellablePositions}
              />
            </div>
          </div>
        </section>

        {desktop.aside}
      </div>

      <DirectSaleMobileBar
        product={product}
        sizes={sizes}
        sheet={mobileSheet}
        onSheetChange={setMobileSheet}
        onOpenTool={openTool}
        hidden={activeTool !== null || editMode}
        above={viewDots}
      />
      {isPhone && (
        <input
          ref={uploadInputRef}
          type="file"
          accept={DIRECT_SALE_UPLOAD_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void phoneUpload.handleFile(file);
          }}
        />
      )}
      {isPhone && addModalOpen && (
        <DirectSaleAddModal
          onPick={openTool}
          onClose={() => setAddModalOpen(false)}
        />
      )}
      {desktop.dialogs}
    </div>
  );
}

/**
 * Duplicate control for the bound text object — lives in the panel header
 * next to the close button, giving R6's duplicate commitment a home for
 * text (images have theirs in the action bar).
 */
function TextDuplicateButton() {
  const { t } = useTranslation();
  const { duplicateSelected } = useObjectOperations();

  return (
    <button
      type="button"
      aria-label={t('directSale.design.duplicate')}
      title={t('directSale.design.duplicate')}
      onClick={() => void duplicateSelected()}
      className="flex h-7 w-7 items-center justify-center rounded-control text-mute transition-colors hover:bg-paper-2"
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  );
}
